import React, { useMemo } from "react";
import { productionApi } from "../../../services/reportsService";
import { useReportData } from "../useReportData";
import ServerTable from "../ServerTable";
import {
  AlertStrip,
  BarList,
  ColumnChart,
  DonutChart,
  ErrorState,
  Funnel,
  KpiGrid,
  Leaderboard,
  Panel,
  ReportSkeleton,
  Row,
  SeriesLegend,
  StatusBadge,
  TrendChart,
  TablePanel,
  DistTable,
  TrendTable,
  cols,
  useReportContext,
} from "../ReportKit";
import {
  PALETTE,
  applyPrevious,
  buildKpis,
  formatDate,
  formatNumber,
  getPreviousRange,
  summarizeOperations,
} from "../reportUtils";

const DEFS = [
  {
    key: "totalOperations",
    label: "All jobs",
    desc: "Production jobs {p}",
    icon: "mdi:cog-sync-outline",
  },
  {
    key: "completedOperations",
    label: "Finished jobs",
    desc: "Done {p}",
    icon: "mdi:check-circle-outline",
  },
  {
    key: "inProgressOperations",
    label: "Jobs running",
    desc: "Being worked on now",
    icon: "mdi:progress-clock",
  },
  {
    key: "pendingOperations",
    label: "Jobs waiting",
    desc: "Not started yet",
    icon: "mdi:timer-sand",
  },
  {
    key: "completionRate",
    label: "Jobs finished",
    desc: "Out of every 100 jobs",
    unit: "PERCENT",
    icon: "mdi:percent-outline",
  },
  {
    key: "qcPassRate",
    label: "Quality passed",
    desc: "Out of every 100 checked, how many passed",
    unit: "PERCENT",
    icon: "mdi:shield-check-outline",
  },
  {
    key: "qcFailed",
    label: "Quality failed",
    desc: "Jobs that did not pass the check",
    good: "DOWN",
    icon: "mdi:shield-alert-outline",
  },
  {
    key: "returnedOperations",
    label: "Sent back",
    desc: "Returned to be done again",
    good: "DOWN",
    icon: "mdi:undo-variant",
  },
  {
    key: "avgDurationHours",
    label: "Time per job",
    desc: "Average hours to finish one job",
    unit: "HOURS",
    good: "DOWN",
    icon: "mdi:clock-outline",
  },
  {
    key: "checklistCompletion",
    label: "Checklist done",
    desc: "Checklist tasks that are ticked",
    unit: "PERCENT",
    icon: "mdi:format-list-checks",
  },
];

const minutes = (v) => {
  const m = Number(v);
  if (!Number.isFinite(m) || m <= 0) return "—";
  return m >= 60
    ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m`
    : `${Math.round(m)}m`;
};

const OperationsView = ({ params, refreshKey }) => {
  const { view } = useReportContext();
  const dates = { startDate: params.startDate, endDate: params.endDate };
  const { data, loading, error, reload } = useReportData(async () => {
    const prevRange = getPreviousRange(dates);
    const [cur, prev] = await Promise.all([
      productionApi.operationSummary(dates),
      productionApi.operationSummary(prevRange).catch(() => null),
    ]);
    return {
      cur: summarizeOperations(cur, dates),
      prev: prev ? summarizeOperations(prev, prevRange) : null,
    };
  }, [params.startDate, params.endDate, refreshKey]);
  const tableParams = useMemo(() => dates, [params.startDate, params.endDate]); // eslint-disable-line

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <ReportSkeleton />;

  const { cur, prev } = data;
  const kpis = applyPrevious(buildKpis(cur.kpis, DEFS), prev?.kpis);
  const trend = [
    { key: "started", label: "Started", color: PALETTE[0], type: "area" },
    { key: "completed", label: "Completed", color: PALETTE[1], type: "area" },
  ];
  const columns = [
    {
      key: "productName",
      header: "Product",
      render: (v) => <strong>{v}</strong>,
    },
    { key: "categoryName", header: "Category" },
    { key: "stageName", header: "Stage" },
    { key: "employeeName", header: "Employee" },
    {
      key: "status",
      header: "Status",
      render: (v) => <StatusBadge value={v} />,
    },
    { key: "qcStatus", header: "QC", render: (v) => <StatusBadge value={v} /> },
    {
      key: "operationsStartedAt",
      header: "Started",
      render: (v) => formatDate(v, true),
    },
    {
      key: "operationsCompletedAt",
      header: "Completed",
      render: (v) => formatDate(v, true),
    },
    {
      key: "durationMinutes",
      header: "Duration",
      align: "right",
      render: minutes,
      exportValue: (r) => r.durationMinutes,
    },
    { key: "quantityProcessed", header: "Quantity", align: "right" },
    { key: "remarks", header: "Remarks" },
  ];

  const DONE = /COMPLET|DONE|FINISH|CLOSED/i;
  const PEND = /PENDING|ASSIGN|NEW|WAIT/i;
  const PROG = /PROGRESS|START|RUNNING|ONGOING|ACTIVE/i;
  const T = (title, how, filter) => ({
    modal: {
      title,
      how,
      columns,
      fetcher: productionApi.operationTable,
      params: dates,
      ...(filter ? { filter } : {}),
    },
  });
  const details = {
    totalOperations: {
      scrollTo: "tbl-main",
      ...T("All jobs", "These are all the production jobs {p}."),
    },
    completedOperations: T("Finished jobs", "These jobs are done.", (r) =>
      DONE.test(r.status || ""),
    ),
    inProgressOperations: T(
      "Jobs running",
      "These jobs are being worked on now.",
      (r) => PROG.test(r.status || ""),
    ),
    pendingOperations: T(
      "Jobs waiting",
      "These jobs have not started yet.",
      (r) => PEND.test(r.status || ""),
    ),
    completionRate: {
      scrollTo: "tbl-main",
      ...T(
        "All jobs",
        "Finished jobs divided by all jobs. These are all the jobs.",
      ),
    },
    qcPassRate: T(
      "Jobs that passed quality",
      "These jobs passed the quality check.",
      (r) => /PASS|APPROV/i.test(r.qcStatus || ""),
    ),
    qcFailed: T(
      "Jobs that failed quality",
      "These jobs did not pass the quality check.",
      (r) => /FAIL|REJECT/i.test(r.qcStatus || ""),
    ),
    avgDurationHours: T(
      "Jobs with their time",
      "These jobs have a start and a finish time. The average of their time is shown.",
      (r) => Number(r.durationMinutes) > 0,
    ),
  };

  return (
    <div className={`rp-view ${loading ? "rp-refreshing" : ""}`}>
      <AlertStrip
        items={[
          cur.kpis.qcFailed > 0 && {
            tone: "bad",
            icon: "mdi:shield-alert-outline",
            text: `${cur.kpis.qcFailed} operations failed quality check`,
          },
          cur.bottleneck !== "—" && {
            tone: "warn",
            icon: "mdi:speedometer-slow",
            text: `Slowest stage: ${cur.bottleneck}`,
          },
        ]}
      />
      <KpiGrid kpis={kpis} details={details} />
      {view === "table" ? (
        <>
          <ServerTable
            id="tbl-main"
            title="Operation records"
            columns={columns}
            params={tableParams}
            fetcher={productionApi.operationTable}
            searchable={false}
            sortable={false}
            exportName="operations"
          />

          <div className="rp-tables">
            <TrendTable
              title="Jobs by date"
              data={cur.trend}
              series={trend}
              granularity="DAY"
            />
            <TablePanel
              title="Jobs in each stage"
              exportName="stages"
              columns={[
                cols.text("stage", "Stage"),
                cols.num("total", "Jobs"),
                cols.num("done", "Finished"),
                cols.num("pending", "Waiting"),
                cols.num("avg", "Average time", "HOURS"),
              ]}
              rows={cur.byStage.map((s) => ({
                stage: s.name,
                total: s.total,
                done: s.done,
                pending: s.pending,
                avg: s.avgDuration || null,
              }))}
            />
            <DistTable
              title="By status"
              data={cur.byStatus}
              nameHeader="Status"
              valueHeader="Jobs"
            />
            <DistTable
              title="Quality check results"
              data={cur.qc}
              nameHeader="Result"
              valueHeader="Jobs"
            />
            <TablePanel
              title="Employee output"
              exportName="employees"
              columns={[
                cols.text("name", "Person"),
                cols.num("total", "Jobs"),
                cols.num("done", "Finished"),
                cols.num("qc", "Quality passed", "PERCENT"),
                cols.num("avg", "Average time", "HOURS"),
              ]}
              rows={cur.byEmployee.map((e) => ({
                name: e.name,
                total: e.total,
                done: e.done,
                qc: e.qcPassRate,
                avg: e.avgDuration || null,
              }))}
            />
            <DistTable
              title="By category"
              data={cur.byCategory}
              nameHeader="Category"
              valueHeader="Jobs"
            />
            <DistTable
              title="By product"
              data={cur.byProduct}
              nameHeader="Product"
              valueHeader="Jobs"
            />
            <DistTable
              title="Time taken per job"
              data={cur.durationBuckets}
              nameHeader="Time"
              valueHeader="Jobs"
            />
          </div>
        </>
      ) : (
        <>
          <Row layout="2-1">
            <Panel
              title="Operations over time"
              actions={<SeriesLegend series={trend} />}
            >
              <TrendChart data={cur.trend} series={trend} granularity="DAY" />
            </Panel>
            <Panel title="By status">
              <DonutChart data={cur.byStatus} />
            </Panel>
          </Row>

          <Row layout="1-1">
            <Panel
              title="Work in each stage"
              subtitle="Operations and average time per stage"
            >
              <Funnel
                stages={cur.byStage.map((s) => ({
                  name: s.name,
                  count: s.total,
                  days: s.avgDuration ? s.avgDuration / 24 : null,
                }))}
              />
            </Panel>
            <Panel title="Quality check results">
              <DonutChart data={cur.qc} />
            </Panel>
          </Row>

          <Row layout="2-1">
            <Panel title="Employee output" subtitle="Operations handled">
              <Leaderboard
                primaryLabel="operations"
                rows={cur.byEmployee.map((e) => ({
                  name: e.name,
                  primary: e.total,
                  metrics: [
                    { label: "Completed", value: e.done },
                    { label: "QC pass", value: e.qcPassRate, unit: "PERCENT" },
                    {
                      label: "Avg time",
                      value: e.avgDuration || null,
                      unit: "HOURS",
                    },
                  ],
                }))}
              />
            </Panel>
            <Panel title="Time per operation">
              <ColumnChart data={cur.durationBuckets} color={PALETTE[2]} />
            </Panel>
          </Row>

          <Row layout="1-1">
            <Panel title="By category">
              <BarList data={cur.byCategory} color={PALETTE[3]} />
            </Panel>
            <Panel
              title="By product"
              subtitle={`Top ${formatNumber(cur.byProduct.length)}`}
            >
              <BarList data={cur.byProduct} color={PALETTE[5]} />
            </Panel>
          </Row>
        </>
      )}
    </div>
  );
};

export default OperationsView;
